import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Barcode, PaperPlaneTilt, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_OPTIONS = [
  { value: '', label: 'Not sure yet' },
  { value: 'starter', label: 'Starter — A$99/mo' },
  { value: 'grow', label: 'Grow — A$249/mo' },
  { value: 'core', label: 'Core — A$499/mo' },
  { value: 'scale', label: 'Scale — A$999/mo' },
  { value: 'business', label: 'Business — A$1,999/mo' },
  { value: 'enterprise', label: 'Enterprise — Custom' },
];

const SIZE_OPTIONS = [
  '', 'Solo (just me)', '2-5 staff', '6-20 staff', '21-50 staff', '51-200 staff', '200+ staff',
];

export default function RequestDemo() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    company_size: '',
    use_case: '',
    expected_orders_per_month: '',
    expected_warehouses: '',
    interested_plan: params.get('plan') || '',
    preferred_time: '',
    message: '',
    source: 'request-demo-page',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error('Company, name, and work email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/marketing/demo-request`, form);
      setSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your request — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="request-demo-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-card flex items-center justify-center">
              <Barcode weight="bold" className="text-brand" size={28} />
            </div>
            <span className="text-white font-bold text-3xl tracking-tight">STOCKARIS</span>
          </div>
          <p className="mt-6 text-white/80 text-lg">See Stockaris run your warehouse before you spend a dollar.</p>
          <div className="mt-12 space-y-5 text-white/90">
            {[
              { h: '20-minute walkthrough', p: 'A senior product specialist walks you through the workflow that matches your business — picking, 3PL billing, multi-warehouse, you name it.' },
              { h: 'Bring your data', p: 'Send us a sample SKU list or PO file and we\'ll show how it imports — live, on screen, no slides.' },
              { h: 'No card. No pressure.', p: 'You\'ll get a quote, a feature map, and time to think. We only follow up when you ask us to.' },
            ].map((b) => (
              <div key={b.h} className="flex items-start gap-3">
                <CheckCircle weight="fill" className="text-white/80 mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-bold text-base">{b.h}</div>
                  <p className="text-white/70 text-xs leading-relaxed mt-0.5">{b.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-white/60 text-xs">
          Already paying for a WMS? Ask us about migration credits — we\u2019ll cover the cost of switching.
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-xl">
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <div className="w-10 h-10 bg-brand flex items-center justify-center">
              <Barcode weight="bold" className="text-white" size={24} />
            </div>
            <span className="font-bold text-2xl tracking-tight">STOCKARIS</span>
          </div>

          <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground mb-4" data-testid="request-demo-back-link">
            <ArrowLeft size={12} weight="bold" /> Back
          </Link>

          {submitted ? (
            <div className="bg-card border border-border p-8 text-center" data-testid="request-demo-submitted">
              <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Got it — we'll reach out soon.</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Your demo request <span className="font-mono text-foreground">#{submitted.id?.slice(-8)}</span> has been logged.
                A specialist will email you at <span className="font-mono text-foreground">{form.email}</span> within one business day to schedule your walkthrough.
              </p>
              <div className="flex gap-2 justify-center">
                <Link to="/pricing">
                  <Button variant="outline" className="rounded-none" data-testid="request-demo-pricing-link">See pricing</Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-brand hover:bg-brand/90 text-white rounded-none" data-testid="request-demo-signup-link">Sign up now instead</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border p-8">
              <h1 className="text-2xl font-bold mb-2">Request a demo</h1>
              <p className="text-sm text-muted-foreground mb-6">Tell us a bit about your operation. A specialist will reach out within one business day.</p>

              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline">Company *</Label>
                    <Input data-testid="demo-company" value={form.company_name} onChange={set('company_name')} />
                  </div>
                  <div>
                    <Label className="overline">Your name *</Label>
                    <Input data-testid="demo-contact" value={form.contact_name} onChange={set('contact_name')} />
                  </div>
                  <div>
                    <Label className="overline">Work email *</Label>
                    <Input data-testid="demo-email" type="email" value={form.email} onChange={set('email')} />
                  </div>
                  <div>
                    <Label className="overline">Phone</Label>
                    <Input data-testid="demo-phone" value={form.phone} onChange={set('phone')} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
                  <div>
                    <Label className="overline">Company size</Label>
                    <select data-testid="demo-size" value={form.company_size} onChange={set('company_size')} className="w-full mt-1 px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:border-brand">
                      {SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s || 'Select...'}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="overline">Plan you're considering</Label>
                    <select data-testid="demo-plan" value={form.interested_plan} onChange={set('interested_plan')} className="w-full mt-1 px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:border-brand">
                      {PLAN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="overline">Orders / month</Label>
                    <Input data-testid="demo-orders" value={form.expected_orders_per_month} onChange={set('expected_orders_per_month')} placeholder="e.g. 5,000" />
                  </div>
                  <div>
                    <Label className="overline">Warehouses</Label>
                    <Input data-testid="demo-warehouses" value={form.expected_warehouses} onChange={set('expected_warehouses')} placeholder="e.g. 2" />
                  </div>
                </div>

                <div>
                  <Label className="overline">What problem are you trying to solve?</Label>
                  <textarea
                    data-testid="demo-message"
                    value={form.message}
                    onChange={set('message')}
                    rows={4}
                    placeholder="e.g. Outgrew QuickBooks for inventory; need 3PL billing for 6 merchants; multi-warehouse rollout in March..."
                    className="w-full mt-1 p-2 border border-border bg-background text-sm rounded-none font-sans resize-y"
                  />
                </div>

                <div>
                  <Label className="overline">Preferred time for the call</Label>
                  <Input data-testid="demo-time" value={form.preferred_time} onChange={set('preferred_time')} placeholder="e.g. Weekdays 10am AEST" />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="demo-submit"
                  className="w-full bg-brand hover:bg-brand/90 text-white font-bold rounded-none py-3"
                >
                  {submitting ? 'Sending…' : (<><PaperPlaneTilt size={16} weight="bold" className="mr-2" /> Request my demo</>)}
                </Button>

                <p className="text-[11px] text-muted-foreground text-center">
                  Ready to start now? <Link to="/signup" className="text-brand underline">Skip the call and sign up →</Link>
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
